import { createSlice } from "@reduxjs/toolkit";
import {
  getAdminSessionsBuilder,
  getChatHistoryBuilder,
  createChatSessionBuilder,
  sendMessageBuilder,
  closeChatSessionBuilder,
  assignAgentBuilder,
  getSupportAgentsBuilder,
  sendEmailFallbackBuilder,
  updateAgentStatusBuilder,
  getSupportStatsBuilder,
} from "./builder";

const initialState = {
  adminSessions: {
    data: [],
    loading: false,
    error: null,
  },
  chatHistory: {
    data: [],
    loading: false,
    error: null,
  },
  createSession: {
    data: null,
    loading: false,
    error: null,
  },
  sendMessage: {
    data: null,
    loading: false,
    error: null,
  },
  closeSession: {
    data: null,
    loading: false,
    error: null,
  },
  assignAgent: {
    data: null,
    loading: false,
    error: null,
  },
  agents: {
    data: [],
    loading: false,
    error: null,
  },
  emailFallback: {
    data: null,
    loading: false,
    error: null,
  },
  agentStatus: {
    data: null,
    loading: false,
    error: null,
  },
  stats: {
    data: {},
    loading: false,
    error: null,
  },
};

const supportSlice = createSlice({
  name: "support",
  initialState,
  reducers: {
    // Real-time message added via WebSocket
    addMessage: (state, { payload }) => {
      const { chatId, message, replaceTempId } = payload;
      
      // Find the session and add the message
      const session = state.adminSessions.data.find(session => session.id === chatId);
      if (session) {
        if (!session.messages) {
          session.messages = [];
        }
        
        // If replacing a temporary message
        if (replaceTempId) {
          const tempIndex = session.messages.findIndex(msg => msg.id === replaceTempId);
          if (tempIndex !== -1) {
            session.messages[tempIndex] = message;
            session.lastMessage = message.text;
            session.timestamp = message.timestamp;
            return;
          }
        }
        
        // Check for duplicate messages by ID first
        const existingMessage = session.messages.find(msg => {
          // Strict ID check - only consider real backend IDs 
          if (message.id && msg.id && !msg.id.toString().startsWith('temp_') && !message.id.toString().startsWith('temp_')) {
            return msg.id === message.id;
          }
          return false;
        });
        
        if (!existingMessage) {
          session.messages.push(message);
          session.lastMessage = message.text;
          session.timestamp = message.timestamp;
        }
      }
    },
    
    // Update session status
    updateSessionStatus: (state, { payload }) => {
      const { chatId, status } = payload;
      const session = state.adminSessions.data.find(session => session.id === chatId);
      if (session) {
        session.status = status;
      }
    },
    
    // Update typing indicator
    updateTypingIndicator: (state, { payload }) => {
      const { chatId, isTyping, userId } = payload;
      const session = state.adminSessions.data.find(session => session.id === chatId);
      if (session) {
        session.isTyping = isTyping;
        session.typingUserId = isTyping ? userId : null;
      }
    },
    
    // Update online status
    updateUserOnlineStatus: (state, { payload }) => {
      const { userId, isOnline } = payload;
      state.adminSessions.data.forEach(session => {
        if (session.userId === userId) {
          if (!session.user) {
            session.user = {};
          }
          session.user.isOnline = isOnline;
        }
      });
    },
    
    // Remove specific session
    removeSession: (state, { payload }) => {
      const { chatId } = payload;
      state.adminSessions.data = state.adminSessions.data.filter(
        session => session.id !== chatId
      );
    },
    
    // Clear session data
    clearSessionData: (state) => {
      state.adminSessions.data = [];
      state.chatHistory.data = [];
    },
    
    // Clear errors
    clearErrors: (state) => {
      Object.keys(state).forEach(key => {
        if (state[key].error) {
          state[key].error = null;
        }
      });
    },

    // Reset loading states
    resetLoadingStates: (state) => {
      Object.keys(state).forEach(key => {
        if (state[key].loading) {
          state[key].loading = false;
        }
      });
    },
  },
  extraReducers: (builder) => {
    getAdminSessionsBuilder(builder);
    getChatHistoryBuilder(builder);
    createChatSessionBuilder(builder);
    sendMessageBuilder(builder);
    closeChatSessionBuilder(builder);
    assignAgentBuilder(builder);
    getSupportAgentsBuilder(builder);
    sendEmailFallbackBuilder(builder);
    updateAgentStatusBuilder(builder);
    getSupportStatsBuilder(builder);
  },
});

export const {
  addMessage,
  updateSessionStatus,
  updateTypingIndicator,
  updateUserOnlineStatus,
  removeSession,
  clearSessionData,
  clearErrors,
  resetLoadingStates,
} = supportSlice.actions;

// Selectors
export const selectAdminSessions = (state) => state.support.adminSessions;
export const selectChatHistory = (state) => state.support.chatHistory;
export const selectCreateSession = (state) => state.support.createSession;
export const selectSendMessage = (state) => state.support.sendMessage;
export const selectCloseSession = (state) => state.support.closeSession;
export const selectAssignAgent = (state) => state.support.assignAgent;
export const selectSupportAgents = (state) => state.support.agents;
export const selectEmailFallback = (state) => state.support.emailFallback;
export const selectAgentStatus = (state) => state.support.agentStatus;
export const selectSupportStats = (state) => state.support.stats;

export default supportSlice.reducer;